<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';

import type { LevelChoiceDto, Locales } from '@/services/types/api.types';
import { useFormsStore } from '@/stores';
import { useI18n } from 'vue-i18n';

import ReorderableTable from '@/components/ReorderableTable.vue';
import MatrixChoiceEditDialog from '@/components/dialogs/MatrixChoiceEditDialog.vue';
import { getTranslation } from '@/composables/data-display-utils';

const $q = useQuasar();
const { t } = useI18n();

const formsStore = useFormsStore();

const props = defineProps<{
  rows: LevelChoiceDto[];
  formId: number;
  stepId: number;
  canChoicesBeDeleted?: boolean;
  languageEnabled: Locales[];
}>();

const columns = ref([
  {
    name: 'label',
    label: t('form.matrix-choice.fields.label'),
    align: 'left',
    field: (row: LevelChoiceDto) => getTranslation(row, 'label', props.languageEnabled),
  },
  {
    name: 'value',
    label: t('form.matrix-choice.fields.value'),
    align: 'left',
    field: (row: LevelChoiceDto) => row.value,
  },
]);

const handleRowClick = (row: LevelChoiceDto) => {
  $q.dialog({
    component: MatrixChoiceEditDialog,
    componentProps: {
      mode: 'edit',
      choice: row,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleAddRow = () => {
  const choice = formsStore.createMatrixChoice();
  $q.dialog({
    component: MatrixChoiceEditDialog,
    componentProps: {
      mode: 'add',
      choice,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleDeleteRow = (row: LevelChoiceDto) => {
  $q.dialog({
    title: t('form.dialogs.matrix-choice.delete-title'),
    message: t('form.dialogs.matrix-choice.delete-message'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    formsStore.deleteMatrixChoice(row, props.formId, props.stepId);
  });
};

const handleReorder = async (source: LevelChoiceDto, target: LevelChoiceDto) => {
  await formsStore.saveMatrixChoice(source, props.formId, props.stepId);
  await formsStore.saveMatrixChoice(target, props.formId, props.stepId);
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="props.rows"
      :columns="columns"
      :title="$t('form.matrix-choice.list-title')"
      itemKey="id"
      disable-orderable
      :disable-delete-button="true"
      :action-menu-items="[
        {
          action: 'delete-row',
          label: t('global.label.delete'),
          disabled: !props.canChoicesBeDeleted,
          tooltip: !props.canChoicesBeDeleted ? t('global.notifications.choice-prohibit-delete') : undefined,
        },
      ]"
      @rowClick="handleRowClick"
      @reorder="handleReorder"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    >
    </reorderable-table>
  </div>
</template>
